import type { ProspectResponse } from '@common/types/prospects';
import type {
    CreateVerificationRequest,
    EnhancedVerificationRequest,
} from '@common/types/treefort';
import { getIsIntakeFormV2 } from '@common/utils/intakeFormVersion';
import { isValidEmail } from '@common/utils/isValidEmail';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    Modal,
    Stack,
    type SxProps,
    TextField,
    type Theme,
    Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import LoadingButton from '@/components/Buttons/LoadingButton';
import SVGSpinner from '@/components/Buttons/SVGSpinner';
import CustomPhoneInput from '@/components/CustomPhoneInput';
import DataTable, { type Column } from '@/components/DataTable';
import { useProspectIntakeForm, useVerificationRequests } from '@/queries/prospectDetails';
import { getDownloadLink, getFileDetails } from '@/services/FileService';
import {
    createCancelIDVerification,
    createIDVerificationEmail,
    reInitiateIDVerification,
    updateIDVerification,
} from '@/services/TreefortService';
import { theme } from '@/theme';
import { convertUTCDateToEST, extractContactDetails } from '@/utils';
import { getPhoneValidationError } from '@/utils/phone';
import { errorSnackbar, successSnackbar } from '@/utils/snackbar';
import {
    buildContactEmailNameMap,
    buildContactNameMap,
    type IDVerificationClient,
    mapContactResponsesToIDVClients,
    resolveIdVerificationUserName,
} from './idVerificationContacts';

interface ClientData {
    id: string;
    userName: string;
    email: string;
    phoneNumber: string;
    countryCode: string;
    dialCode: number;
    idVerification: boolean;
    bankReview: boolean;
    errors: {
        email?: string;
        phone?: string;
    };
}

function buildClientStateFromRow(rowData: {
    id: string;
    userName?: string;
    email?: string;
    phoneNumber?: string;
    countryCode?: string;
    dialCode?: number;
    isSecondaryIdRequired?: boolean;
    isBankingVerificationRequired?: boolean;
}): ClientData[] {
    return [
        {
            id: rowData.id,
            userName: rowData.userName ?? '',
            email: rowData.email ?? '',
            phoneNumber: rowData.phoneNumber ?? '',
            countryCode: rowData.countryCode ?? 'ca',
            dialCode: rowData.dialCode ?? 1,
            idVerification: rowData.isSecondaryIdRequired ?? false,
            bankReview: rowData.isBankingVerificationRequired ?? false,
            errors: {},
        },
    ];
}

interface Props {
    prospectId?: string;
    prospect: ProspectResponse;
}

export default function IdVerification({ prospectId, prospect }: Props) {
    const { data: intakeForm } = useProspectIntakeForm(prospectId);
    const {
        data: verificationRequestResponseData = [],
        isLoading: apiLoading,
        refetch: refetchVerificationRequests,
    } = useVerificationRequests(prospectId);
    const intakeFormData = intakeForm?.data;
    const [page, setPage] = useState(0);
    const [isResendModalOpen, setIsResendModalOpen] = useState(false);
    const isIntakeFormV2 = getIsIntakeFormV2(prospect.practiceArea, prospect.matterType);

    const legacyExtractedDetails = useMemo<IDVerificationClient[]>(() => {
        if (!intakeFormData?.formResponse) return [];

        try {
            const parsed = JSON.parse(intakeFormData.formResponse);
            return extractContactDetails(parsed).map((contact) => ({
                ...contact,
                id: contact.id.toString(),
            }));
        } catch (error) {
            console.error('Error parsing formResponse:', error);
            return [];
        }
    }, [intakeFormData]);

    const extractedDetails = useMemo(
        () =>
            isIntakeFormV2
                ? mapContactResponsesToIDVClients(intakeFormData?.contactResponses)
                : legacyExtractedDetails,
        [intakeFormData?.contactResponses, isIntakeFormV2, legacyExtractedDetails],
    );

    const intakeContactDetails = useMemo(() => {
        return {
            details: intakeFormData?.userContactDetails ?? [],
            prospectVersionId: intakeFormData?.prospectVersion?.prospectVersionId ?? null,
        };
    }, [intakeFormData]);

    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedRow, setSelectedRow] = useState<EnhancedVerificationRequest | null>(null);
    const [isLoadingButton, setIsLoadingButton] = useState<boolean>(false);
    const [idVerification, setIdVerification] = useState<boolean>(false);
    const [bankReview, setBankReview] = useState<boolean>(false);
    const [showResendIDVDailog, setShowResendIDVDailog] = useState<boolean>(false);
    const [isInitiateIDV, setIsInitiateIDV] = useState<boolean>(false);
    const [client, setClient] = useState(extractedDetails);
    const [errors, setErrors] = useState<{ phone: boolean }>({
        phone: false,
    });
    const [clientState, setClientState] = useState<ClientData[]>([]);

    const [isUpdateIDVModalOpen, setIdUpdateIDVModalOpen] = useState<boolean>(false);

    const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});

    const handlePhoneChange = (phone: string, id: string) => {
        setClient((prev) => prev.map((c) => (c.id === id ? { ...c, phone } : c)));
        setErrors((prev) => ({ ...prev, [id]: false }));
    };

    const handleCountryChange = (countryData: { code: string; dialCode: string }, id: string) => {
        setClient((prev) =>
            prev.map((c) =>
                c.id === id
                    ? {
                          ...c,
                          countryCode: countryData.code,
                          dialCode: Number(countryData.dialCode),
                      }
                    : c,
            ),
        );
        setErrors((prev) => ({ ...prev, [id]: false }));
    };

    const validateClient = () => {
        let hasError = false;

        setClientState((prevClients) =>
            prevClients.map((client) => {
                const email = client.email.trim();
                const phone = client.phoneNumber.trim();
                const errors: { email?: string; phone?: string } = {};

                if (email === '') {
                    errors.email = 'Please enter email';
                    hasError = true;
                } else if (!isValidEmail(email)) {
                    errors.email = 'Invalid email format';
                    hasError = true;
                }

                const phoneError = getPhoneValidationError(phone, client.countryCode);
                if (phoneError) {
                    errors.phone = phoneError;
                    hasError = true;
                }

                return {
                    ...client,
                    errors,
                };
            }),
        );

        return hasError;
    };

    const handleEmailChange = (id: string, value: string) => {
        setClientState((prev) =>
            prev.map((c) =>
                c.id === id ? { ...c, email: value, errors: { ...c.errors, email: '' } } : c,
            ),
        );
    };

    const handleClientDataPhoneChange = (id: string, phoneNumber: string) => {
        setClientState((prev) =>
            prev.map((c) =>
                c.id === id ? { ...c, phoneNumber, errors: { ...c.errors, phone: '' } } : c,
            ),
        );
    };

    const handleClientDataCountryChange = (
        id: string,
        countryData: { code: string; dialCode: string },
    ) => {
        setClientState((prev) =>
            prev.map((c) =>
                c.id === id
                    ? {
                          ...c,
                          countryCode: countryData.code,
                          dialCode: Number(countryData.dialCode),
                          errors: { ...c.errors, phone: '' },
                      }
                    : c,
            ),
        );
    };

    const toggleCheckbox = (id: string, field: 'idVerification' | 'bankReview') => {
        setClientState((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: !c[field] } : c)));
    };

    const modalStyles: SxProps<Theme> = {
        position: 'absolute' as const,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: '800px',
        maxHeight: '500px',
        width: '100%',
        bgcolor: 'white',
        borderRadius: '8px',
    };

    const modalBodyStyles: SxProps<Theme> = {
        padding: '24px 24px 16px 24px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    };

    const handleOpenDialog = (rowData: EnhancedVerificationRequest) => {
        setSelectedRow(rowData);
        setOpenDialog(true);
    };

    const handleIsResendModal = async (rowData: EnhancedVerificationRequest) => {
        setSelectedRow(rowData);
        setIsResendModalOpen(true);
    };

    const handleResendConfirmationDailog = async (rowData: EnhancedVerificationRequest) => {
        setSelectedRow(rowData);
        setShowResendIDVDailog(true);
    };

    const closeResendConfirmationDailog = () => {
        setShowResendIDVDailog(false);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedRow(null);
    };

    const handleUpdateIDVDetails = async (rowData: EnhancedVerificationRequest) => {
        setSelectedRow(rowData);
        setClientState(buildClientStateFromRow(rowData));
        setIdUpdateIDVModalOpen(true);
    };

    const openReInitiateIdVerification = () => {
        setClient(extractedDetails.filter((item) => item?.email === selectedRow?.email));
        setIdVerification(false);
        setBankReview(false);
        setIsInitiateIDV(true);
    };

    const handleResendVerification = async () => {
        setIsLoadingButton(true);
        if (!selectedRow?.clientRequestId) return;
        try {
            await reInitiateIDVerification({
                requestId: selectedRow.clientRequestId,
                isSecondaryIdRequired: idVerification,
                isBankingVerificationRequired: bankReview,
            });
            successSnackbar('ID Verification reinitiated successfully!');
            setIsResendModalOpen(false);
            setIsLoadingButton(false);
            await refreshVerificationRequests();
        } catch (error) {
            console.error(error);
            errorSnackbar('Failed to resend ID Verification. Please try again.');
            setIsLoadingButton(false);
        }
    };

    const handleConfirmWithdraw = async () => {
        if (selectedRow) {
            setIsLoadingButton(true);
            try {
                await createCancelIDVerification(selectedRow.clientRequestId);
                successSnackbar('ID Verification cancelled successfully!');
                handleCloseDialog();
                setIsLoadingButton(false);
                await refreshVerificationRequests();
            } catch (error) {
                console.error('Error cancelling ID Verification:', error);
                errorSnackbar('Failed to cancelling ID Verification');
                setIsLoadingButton(false);
            }
        }
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const contactDetails = useMemo(
        () => intakeFormData?.userContactDetails ?? [],
        [intakeFormData],
    );

    const contactMap = useMemo(
        () =>
            isIntakeFormV2
                ? buildContactNameMap(intakeFormData?.contactResponses ?? [], 'contactId')
                : buildContactNameMap(contactDetails ?? [], 'contactDetailId'),
        [contactDetails, intakeFormData?.contactResponses, isIntakeFormV2],
    );

    const emailContactMap = useMemo(
        () =>
            buildContactEmailNameMap(
                contactDetails ?? [],
                isIntakeFormV2 ? intakeFormData?.contactResponses : null,
            ),
        [contactDetails, intakeFormData?.contactResponses, isIntakeFormV2],
    );

    const enhancedRequests = useMemo<EnhancedVerificationRequest[]>(
        () =>
            (verificationRequestResponseData ?? []).map((request) => ({
                ...request,
                userName: resolveIdVerificationUserName(request, contactMap, emailContactMap),
            })),
        [verificationRequestResponseData, contactMap, emailContactMap],
    );

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const refreshVerificationRequests = async () => {
        await refetchVerificationRequests();
    };

    const contentWrapperStyles: SxProps<Theme> = {
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    };

    const tableStyles: SxProps<Theme> = {
        border: `1px solid ${theme.palette.neutral.borderColor}`,
        boxShadow: 'none',
    };

    const statusColors: Record<string, { color: string }> = {
        'Request Pending': { color: '#FFB300' },
        Cancelled: { color: '#F44336' },
        'Review Report': { color: '#4CAF50' },
    };

    const fetchReviewFile = async (fileId: string, fileName?: string) => {
        try {
            const response = await fetch(fileId);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName ?? 'idv-review-report.pdf';
            a.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };

    const handleReviewReportDownload = async (reviewFileId: string) => {
        if (loadingStates[reviewFileId]) return;

        try {
            setLoadingStates((prev) => ({
                ...prev,
                [reviewFileId]: true,
            }));
            const downloadLink = getDownloadLink(reviewFileId);
            const fileDetails = await getFileDetails(reviewFileId);
            fetchReviewFile(downloadLink, fileDetails?.data?.fileName);
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingStates((prev) => ({
                ...prev,
                [reviewFileId]: false,
            }));
        }
    };

    const columns: Column<EnhancedVerificationRequest>[] = [
        {
            id: 'fileNumber',
            label: 'File Number',
        },
        {
            id: 'userName',
            label: 'User Name',
            _render: (rowData) => rowData.userName,
        },
        {
            id: 'email',
            label: 'User Email',
        },
        {
            id: 'requestStatus',
            label: 'IDV Status',
            _render: (rowData) => {
                const status = rowData.requestStatus ?? '';
                const isReviewReport = status === 'Review Report' && rowData.reviewFileId;
                const isLoading = rowData.reviewFileId
                    ? loadingStates[rowData.reviewFileId]
                    : false;

                return (
                    <Stack
                        direction="row"
                        onClick={
                            isReviewReport && rowData.reviewFileId
                                ? () => handleReviewReportDownload(rowData.reviewFileId)
                                : undefined
                        }
                        sx={{
                            fontWeight: 'bold',
                            lineHeight: 1,
                            borderRadius: 1,
                            color: isReviewReport ? '#1E90FF' : statusColors[status]?.color,
                            cursor: isReviewReport ? 'pointer' : 'default',
                            textDecoration: isReviewReport ? 'underline' : 'none',
                            '&:hover': {
                                textDecoration: isReviewReport ? 'underline' : 'none',
                            },
                            opacity: isLoading ? 0.75 : 1,
                        }}
                    >
                        {isLoading && <SVGSpinner stroke="#1E90FF" height={16} width={16} />}
                        {status}
                    </Stack>
                );
            },
        },
        {
            id: 'createdAt',
            label: 'IDV Sent At',
            _render: (rowData) => convertUTCDateToEST(rowData.createdAt, 'M/d/yyyy h:mm a'),
        },
        {
            id: 'verificationLink',
            label: 'IDV Link',
            _render: (rowData) => {
                const link = rowData.verificationLink?.trim();

                const copyToClipboard = () => {
                    if (link) {
                        navigator.clipboard.writeText(link);
                    }
                };

                return (
                    <Box className="flex items-center gap-2">
                        {link && (
                            <IconButton size="small" onClick={copyToClipboard}>
                                <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        )}
                    </Box>
                );
            },
        },
        {
            id: 'withdraw',
            label: '',
            _render: (rowData) => {
                const status = rowData.requestStatus?.trim() ?? '';
                const isResend = status === 'Cancelled' || status === 'Review Report';

                const estDateStr = convertUTCDateToEST(rowData.createdAt, 'yyyy-MM-dd HH:mm:ss');
                const createdAtEST = new Date(estDateStr);
                const now = new Date();

                const diffInMs = now.getTime() - createdAtEST.getTime();
                const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

                const isOldReviewReport = status === 'Review Report' && diffInDays > 14;
                const handleClick = () => {
                    if (isOldReviewReport) {
                        handleResendConfirmationDailog(rowData);
                    } else if (isResend) {
                        handleIsResendModal(rowData);
                    } else {
                        handleOpenDialog(rowData);
                    }
                };
                return (
                    <Button variant="contained" color="primary" size="small" onClick={handleClick}>
                        {isResend ? 'Resend' : 'Withdraw'}
                    </Button>
                );
            },
        },
        {
            id: 'update',
            label: '',
            _render: (rowData) => {
                const status = rowData.requestStatus?.trim() ?? '';

                const handleClick = () => {
                    handleUpdateIDVDetails(rowData);
                };

                return (
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={status !== 'Request Pending'}
                        onClick={handleClick}
                    >
                        Update
                    </Button>
                );
            },
        },
    ];

    const handleReInitiateIDV = async () => {
        const contactDetail = intakeContactDetails?.details?.find(
            (contact) => contact.email === client[0]?.email,
        );
        const requesterClientRequestId =
            client[0]?.contactDetailId ?? contactDetail?.contactDetailId;

        if (!requesterClientRequestId) {
            throw new Error('Unable to find contact detail for ID verification request');
        }

        const profiles = [
            {
                requesterClientRequestId,
                email: client[0]?.email,
                phoneNumber: client[0]?.phone,
                countryCode: client[0]?.countryCode || 'ca',
                dialCode: client[0]?.dialCode || 1,
                isSecondaryIdRequired: idVerification || false,
                isBankingVerificationRequired: bankReview || false,
            },
        ];

        await handleSendIDVerification({
            prospectVersionId: intakeContactDetails?.prospectVersionId,
            prospectId: prospect?.prospectId,
            requesterReferenceId: prospect?.quoteApprover?.userId?.toString(),
            initiatorName: prospect?.quoteApprover?.fullName,
            initiatorEmail: prospect?.quoteApprover?.email,
            initiatorOrganizationName: prospect.lawFirm.companyName,
            createNewClientRecord: true,
            profiles,
        });
    };

    const handleUpdateIDVInformation = async () => {
        const hasError = validateClient();
        if (hasError) return;

        const profiles = [
            {
                disableTreefortReviewEmails: true,
                requesterClientRequestId: selectedRow?.requesterClientRequestId,
                clientRequestId: selectedRow?.clientRequestId,
                email: clientState?.[0]?.email,
                phoneNumber: clientState?.[0]?.phoneNumber,
                countryCode: clientState?.[0]?.countryCode || 'ca',
                dialCode: clientState?.[0]?.dialCode || 1,
                isSecondaryIdRequired: clientState?.[0]?.idVerification || false,
                isBankingVerificationRequired: clientState?.[0]?.bankReview || false,
            },
        ];

        const payload = {
            prospectVersionId: intakeContactDetails?.prospectVersionId,
            prospectId: prospect?.prospectId,
            requesterReferenceId: prospect?.quoteApprover?.userId?.toString(),
            initiatorName: prospect?.quoteApprover?.fullName,
            initiatorEmail: prospect?.quoteApprover?.email,
            initiatorOrganizationName: prospect.lawFirm.companyName,
            createNewClientRecord: true,
            fileReferenceId: selectedRow?.fileReferenceId,
            profiles,
        };
        await handleUpdateIDV(selectedRow?.clientRequestId, payload).then(() => {
            setSelectedRow(null);
        });
    };

    const handleSendIDVerification = async (payload: CreateVerificationRequest) => {
        if (prospectId) {
            setIsLoadingButton(true);
            try {
                await createIDVerificationEmail(payload);
                successSnackbar('ID verification has been re-initiated successfully.');
                setIsInitiateIDV(false);
                setIsLoadingButton(false);
                await refreshVerificationRequests();
            } catch (error) {
                console.error(error);
                errorSnackbar('Failed to send ID Verification. Please try again.');
                setIsLoadingButton(false);
            }
        }
    };

    const handleUpdateIDV = async (clientRequestId: string, payload: any) => {
        if (prospectId) {
            setIsLoadingButton(true);
            try {
                await updateIDVerification(clientRequestId, payload);
                successSnackbar('Information has been updated successfully!');
                setIsLoadingButton(false);
                setIdUpdateIDVModalOpen(false);
                await refreshVerificationRequests();
            } catch (error) {
                console.error(error);
                errorSnackbar('Failed to update ID Verification. Please try again.');
                setIsLoadingButton(false);
            }
        }
    };

    return (
        <Box sx={contentWrapperStyles}>
            <Grid container spacing={2}>
                <Grid item xs={12} md={12}>
                    <Typography sx={{ marginBottom: '16px' }} variant="h3">
                        ID Verification Details
                    </Typography>
                    <DataTable
                        generateKeyFrom="clientRequestId"
                        isLoading={apiLoading}
                        columns={columns}
                        rows={enhancedRequests}
                        customStyles={tableStyles}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                </Grid>
            </Grid>
            {openDialog && (
                <Dialog open onClose={handleCloseDialog}>
                    <DialogTitle>Confirm Withdrawal</DialogTitle>
                    <DialogContent>
                        Are you sure you want to cancel this ID Verification?
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog} variant="outlined" color="primary">
                            Cancel
                        </Button>
                        <LoadingButton
                            loading={isLoadingButton}
                            onClick={handleConfirmWithdraw}
                            variant="contained"
                        >
                            Confirm
                        </LoadingButton>
                    </DialogActions>
                </Dialog>
            )}
            {isResendModalOpen && (
                <Modal open onClose={() => setIsResendModalOpen(false)}>
                    <Box sx={modalStyles}>
                        <Box sx={modalBodyStyles}>
                            <Typography variant="h2">Resend ID Verification?</Typography>
                            <Typography variant="body2" sx={{ marginTop: '8px' }}>
                                Please verify the information below to proceed with ID verification.
                            </Typography>
                            <Box sx={{ marginTop: 3, marginBottom: 2 }}>
                                <Typography
                                    variant="h6"
                                    sx={{ color: 'text.secondary', fontWeight: 600, mb: 1 }}
                                >
                                    {selectedRow?.userName}
                                </Typography>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ color: 'text.secondary', mb: 0.5 }}
                                >
                                    {selectedRow?.email}
                                </Typography>
                                <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                                    {selectedRow?.phoneNumber}
                                </Typography>
                            </Box>
                            <Box>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={idVerification}
                                            onChange={() => setIdVerification((prev) => !prev)}
                                        />
                                    }
                                    label="Request Second ID"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={bankReview}
                                            onChange={() => setBankReview((prev) => !prev)}
                                        />
                                    }
                                    label="Include Banking Verification"
                                />
                            </Box>
                        </Box>
                        <Divider />
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                padding: '16px',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    onClick={() => setIsResendModalOpen(false)}
                                >
                                    Cancel
                                </Button>
                                <LoadingButton
                                    variant="contained"
                                    loading={isLoadingButton}
                                    onClick={handleResendVerification}
                                >
                                    Confirm
                                </LoadingButton>
                            </Box>
                        </Box>
                    </Box>
                </Modal>
            )}
            {showResendIDVDailog && (
                <Dialog open onClose={closeResendConfirmationDailog}>
                    <DialogTitle>Re-initiate ID Verification?</DialogTitle>
                    <DialogContent>
                        <strong>{selectedRow?.userName}</strong>
                        {
                            ' has already completed the ID Verification process, and more than 14 days have passed since its completion.'
                        }
                        <br />
                        <br />
                        Re-initiating the ID Verification will start a new process for the client,
                        and an additional charge will apply.
                        <br />
                        <br />
                        Do you want to continue?
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={closeResendConfirmationDailog}
                            variant="outlined"
                            color="primary"
                        >
                            Cancel
                        </Button>
                        <LoadingButton
                            loading={isLoadingButton}
                            onClick={() => {
                                closeResendConfirmationDailog();
                                openReInitiateIdVerification();
                            }}
                            variant="contained"
                        >
                            Confirm
                        </LoadingButton>
                    </DialogActions>
                </Dialog>
            )}
            {isInitiateIDV && (
                <Modal open onClose={() => setIsInitiateIDV(false)}>
                    <Box sx={modalStyles}>
                        <Box sx={modalBodyStyles}>
                            <Typography variant="h2">Re-Initiate ID Verification?</Typography>
                            <Typography variant="body2" sx={{ marginTop: '8px' }}>
                                Please verify the information below to proceed with ID verification.
                            </Typography>
                            <Box sx={{ padding: '16px', paddingLeft: 0 }}>
                                {client?.map((c) => (
                                    <Box
                                        key={c.id}
                                        sx={{
                                            marginBottom: 2,
                                            padding: 2,
                                            border: '1px solid #ddd',
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            {c.name}
                                        </Typography>
                                        <Box sx={{ mt: 2 }}>
                                            <TextField
                                                label="Email"
                                                value={c.email}
                                                fullWidth
                                                size="small"
                                                disabled
                                            />
                                        </Box>
                                        <Box sx={{ mt: 2 }}>
                                            <CustomPhoneInput
                                                value={c.phone}
                                                selectedCountryCode={c.countryCode || 'ca'}
                                                onChange={(value) => handlePhoneChange(value, c.id)}
                                                onCountryChange={(country) =>
                                                    handleCountryChange(country, c.id)
                                                }
                                                error={!!errors[c.id]?.phone}
                                                helperText={errors[c.id]?.phone}
                                            />
                                        </Box>
                                        <Box sx={{ mt: 2 }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={idVerification}
                                                        onChange={() =>
                                                            setIdVerification((prev) => !prev)
                                                        }
                                                    />
                                                }
                                                label="Request Second ID"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={bankReview}
                                                        onChange={() =>
                                                            setBankReview((prev) => !prev)
                                                        }
                                                    />
                                                }
                                                label="Include Banking Verification"
                                            />
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                        <Divider />
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                padding: '16px',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setIsInitiateIDV(false);
                                        setSelectedRow(null);
                                    }}
                                >
                                    Cancel
                                </Button>
                                <LoadingButton
                                    variant="contained"
                                    loading={isLoadingButton}
                                    onClick={handleReInitiateIDV}
                                >
                                    Confirm
                                </LoadingButton>
                            </Box>
                        </Box>
                    </Box>
                </Modal>
            )}
            {isUpdateIDVModalOpen && (
                <Modal open onClose={() => setIdUpdateIDVModalOpen(false)}>
                    <Box sx={modalStyles}>
                        <Box sx={modalBodyStyles}>
                            <Typography variant="h2">Update IDV Information?</Typography>
                            <Typography variant="body2" sx={{ marginTop: '8px' }}>
                                Please verify the information below to proceed with ID verification.
                            </Typography>
                            <Box sx={{ padding: '16px', paddingLeft: 0 }}>
                                {clientState.map((c) => (
                                    <Box
                                        key={c.id}
                                        sx={{
                                            marginBottom: 2,
                                            padding: 2,
                                            border: '1px solid #ddd',
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            {c.userName}
                                        </Typography>

                                        <Box sx={{ mt: 2 }}>
                                            <TextField
                                                label="Email"
                                                value={c.email}
                                                onChange={(e) =>
                                                    handleEmailChange(c.id, e.target.value)
                                                }
                                                error={!!c.errors.email}
                                                helperText={c.errors.email}
                                                fullWidth
                                                size="small"
                                            />
                                        </Box>

                                        <Box sx={{ mt: 2 }}>
                                            <CustomPhoneInput
                                                value={c.phoneNumber}
                                                selectedCountryCode={c.countryCode || 'ca'}
                                                onChange={(value) =>
                                                    handleClientDataPhoneChange(c.id, value)
                                                }
                                                onCountryChange={(country) =>
                                                    handleClientDataCountryChange(c.id, country)
                                                }
                                                error={!!c.errors.phone}
                                                helperText={c.errors.phone}
                                            />
                                        </Box>

                                        <Box sx={{ mt: 2 }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={c.idVerification}
                                                        onChange={() =>
                                                            toggleCheckbox(c.id, 'idVerification')
                                                        }
                                                    />
                                                }
                                                label="Request Second ID"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={c.bankReview}
                                                        onChange={() =>
                                                            toggleCheckbox(c.id, 'bankReview')
                                                        }
                                                    />
                                                }
                                                label="Include Banking Verification"
                                            />
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                        <Divider />
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                padding: '16px',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setIdUpdateIDVModalOpen(false);
                                        setSelectedRow(null);
                                    }}
                                >
                                    Cancel
                                </Button>
                                <LoadingButton
                                    variant="contained"
                                    loading={isLoadingButton}
                                    onClick={handleUpdateIDVInformation}
                                >
                                    Confirm
                                </LoadingButton>
                            </Box>
                        </Box>
                    </Box>
                </Modal>
            )}
        </Box>
    );
}
